/**
 * Conversations state management.
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../services/api';

export const askQuestion = createAsyncThunk(
  'conversations/ask',
  async ({ question, sessionId, documentIds, folderIds }, { rejectWithValue }) => {
    try {
      const response = await api.post('/conversations/query', {
        question,
        session_id: sessionId,
        document_ids: documentIds && documentIds.length > 0 ? documentIds : null,
        folder_ids: folderIds && folderIds.length > 0 ? folderIds : null,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Query failed');
    }
  }
);

export const fetchSessions = createAsyncThunk(
  'conversations/fetchSessions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/conversations/sessions');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch sessions');
    }
  }
);

export const fetchSessionMessages = createAsyncThunk(
  'conversations/fetchSessionMessages',
  async (sessionId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/conversations/sessions/${sessionId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch session messages');
    }
  }
);

export const createSession = createAsyncThunk(
  'conversations/createSession',
  async (sessionName, { rejectWithValue }) => {
    try {
      const response = await api.post('/conversations/sessions', {
        session_name: sessionName,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to create session');
    }
  }
);

export const deleteSession = createAsyncThunk(
  'conversations/deleteSession',
  async (sessionId, { rejectWithValue }) => {
    try {
      await api.delete(`/conversations/sessions/${sessionId}`);
      return sessionId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to delete session');
    }
  }
);

const conversationsSlice = createSlice({
  name: 'conversations',
  initialState: {
    currentSessionId: null,
    sessions: [],
    currentAnswer: null,
    sessionMessages: [],
    loading: false,
    messagesLoading: false,
    error: null,
  },
  reducers: {
    setCurrentSession: (state, action) => {
      state.currentSessionId = action.payload;
      state.currentAnswer = null;
      state.sessionMessages = [];
    },
    clearError: (state) => {
      state.error = null;
    },
    clearSessionMessages: (state) => {
      state.sessionMessages = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(askQuestion.pending, (state) => {
        state.loading = true;
      })
      .addCase(askQuestion.fulfilled, (state, action) => {
        state.loading = false;
        state.currentAnswer = action.payload;
        state.currentSessionId = action.payload.session_id;
        // Add to session messages for display
        state.sessionMessages.push({
          question: action.meta.arg.question,
          answer: action.payload.answer,
          confidence_score: action.payload.confidence,
          sources: action.payload.sources,
          tokens_used: action.payload.tokens_used,
          created_at: new Date().toISOString(),
        });
      })
      .addCase(askQuestion.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchSessions.fulfilled, (state, action) => {
        state.sessions = action.payload;
      })
      .addCase(fetchSessionMessages.pending, (state) => {
        state.messagesLoading = true;
      })
      .addCase(fetchSessionMessages.fulfilled, (state, action) => {
        state.messagesLoading = false;
        state.sessionMessages = action.payload.messages || [];
        state.currentSessionId = action.payload.id;
      })
      .addCase(fetchSessionMessages.rejected, (state, action) => {
        state.messagesLoading = false;
        state.error = action.payload;
      })
      .addCase(createSession.fulfilled, (state, action) => {
        state.sessions.unshift(action.payload);
        state.currentSessionId = action.payload.id;
        state.sessionMessages = [];
        state.currentAnswer = null;
      })
      .addCase(deleteSession.fulfilled, (state, action) => {
        state.sessions = state.sessions.filter(session => session.id !== action.payload);
        if (state.currentSessionId === action.payload) {
          state.currentSessionId = null;
          state.sessionMessages = [];
          state.currentAnswer = null;
        }
      });
  },
});

export const { setCurrentSession, clearError, clearSessionMessages } = conversationsSlice.actions;
export default conversationsSlice.reducer;
